import { TwAutocompleteOption } from '../../components/TwAutocomplete/TwAutocomplete';
import { LoadLocation, LOCATION_TYPE } from '../../redux/api/load/load.model';
import { caPortCodesRaw, usPortCodesRaw } from './variables';

interface LatLngLiteralVerbose {
  latitude: number;
  longitude: number;
}
export type LatLngArray = [number, number];
export type LatLngString = string;

export type RoutesApiLatLngObj = {
  location: { latLng: LatLngLiteralVerbose };
};

export type LatLng = google.maps.LatLngLiteral | LatLngArray | LatLngString | LatLngLiteralVerbose;

export function toRouteLatLng(latLng: LatLng): RoutesApiLatLngObj {
  // If the latLng is an array i.e. is LatLngArray
  if (Array.isArray(latLng)) {
    return { location: { latLng: { latitude: latLng[0], longitude: latLng[1] } } };
  }

  // If latLng is string i.e. is LatLngString
  if (typeof latLng === 'string') {
    const latLngString = latLng.split(',');
    const latitude = parseFloat(latLngString[0]);
    const longitude = parseFloat(latLngString[1]);
    return { location: { latLng: { latitude, longitude } } };
  }

  // If latLng contains lat in it i.e. is LatLngLiteral
  if ('lat' in latLng) {
    return { location: { latLng: { latitude: latLng.lat, longitude: latLng.lng } } };
  }

  // If latLng contains lat in it i.e. is LatLngLiteralverbose
  return { location: { latLng } };
}

export const getAddressLines = (
  full_address: string,
  city: string
): { address1: string; address2: string } => {
  const arrayWithAddresses = full_address.split(',');
  const address1 = arrayWithAddresses[0];
  const address2Indx = arrayWithAddresses.findIndex((address) => address.includes(city));
  let address2 = '';

  for (let index = 1; index < address2Indx; index++) {
    address2 = address2 + arrayWithAddresses[index];
  }
  return { address1: address1, address2: address2 };
};

type MakeFullAddressParams = {
  address_1: string;
  address_2?: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
};

export const makeFullAddress = (params: MakeFullAddressParams): string => {
  const { address_1, address_2, city, state, zip, country } = params;
  return `${address_1}${address_2 ? ` ${address_2}` : ''}, ${city}, ${state} ${zip}, ${country}`;
};

export const makePartialAddress = (params?: LoadLocation | null): string | null => {
  if (!params) return null;
  const { address_1, address_2, city, state } = params;
  return `${address_1}${address_2 ? ` ${address_2}` : ''}, ${city}, ${state}`;
};

export const locationTypeOptions = (bcFlag: boolean): TwAutocompleteOption[] => {
  const options = [
    { id: 'customer', value: LOCATION_TYPE.CUSTOMER, label: 'Customer location' },
    { id: 'terminal', value: LOCATION_TYPE.TERMINAL, label: 'Terminal' }
  ];

  if (bcFlag) {
    options.push(
      { id: 'us_port', value: LOCATION_TYPE.US_PORT, label: 'US Port of entry' },
      { id: 'ca_port', value: LOCATION_TYPE.CA_PORT, label: 'CA Port of entry' }
    );
  }

  return options;
};

export const getLocationTypeLabel = (value: LOCATION_TYPE | null): string => {
  if (!value) return ' ';
  switch (value) {
    case LOCATION_TYPE.CUSTOMER:
      return 'Customer location';
    case LOCATION_TYPE.TERMINAL:
      return 'Terminal';
    case LOCATION_TYPE.US_PORT:
      return 'US Port of entry';
    case LOCATION_TYPE.CA_PORT:
      return 'CA Port of entry';
    default:
      return ' ';
  }
};

export const portCodeOptions = (): TwAutocompleteOption[] => {
  const usPorts = usPortCodesRaw.map((port) => {
    const label = `${port.code} - ${port.portOfEntry}`;
    return { id: port.code, value: port.code, label };
  });

  const caPorts = caPortCodesRaw.map((port) => {
    const label = `${port.number} - ${port.name}`;
    return { id: port.number, value: port.number, label };
  });

  return [...usPorts, ...caPorts];
};

export const usPortCodeOptions = (): TwAutocompleteOption[] => {
  return usPortCodesRaw.map((port) => {
    const label = `${port.code} - ${port.portOfEntry}`;
    return { id: port.code, value: port.code, label };
  });
};

export const caPortCodeOptions = (): TwAutocompleteOption[] => {
  return caPortCodesRaw.map((port) => {
    const label = `${port.number} - ${port.name}`;
    return { id: port.number, value: port.number, label };
  });
};
